import { LoadingOverlay, Title, Group, Button } from '@mantine/core';
import Breadcrumb, { BreadcrumbItem } from 'components/BreadcrumbsComponent';
import React, { useEffect, useState } from 'react';
import { pathnames } from 'routes/lcms';
import { useCourseStore } from 'store/createCourseStore';
import { useNavigate, useParams } from 'react-router-dom';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import { transformCoursePayload } from 'utils/coursePayloadTransformer';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { courseService } from 'services/lcms/course.service';
import { FormCreateCourseLCMS } from 'types/lcms/courses';
import { useDisclosure } from '@mantine/hooks';
import ModalConfirmation from 'modules/pages/lcms/courses/ModalConfirmation';
import { useFormCourse } from 'hooks/forms/useFormCourse';
import CourseForm from 'components/forms/lcms/course/CourseForm';
import { masterAreaService } from 'services/dacum/masterAreaService';
import { IconCopy } from '@tabler/icons-react';
import CopyCourseModal from 'components/CopyCourseModal';

const CreateCourse = () => {
  const params = useParams();
  const queryClient = useQueryClient();
  const [opened, { open, close }] = useDisclosure(false);
  const [copyModalOpened, { open: openCopyModal, close: closeCopyModal }] = useDisclosure(false);
  const { formData, mode, setFormMode, setDetailCourse } = useCourseStore();
  const { setupFormCourse } = useFormCourse();
  const navigate = useNavigate();
  const { id } = params;

  const [formPayload, setFormPayload] = useState<FormCreateCourseLCMS | null>(null);

  const fetchedEmployeeLecturer = useQuery({
    queryKey: ['smeDataEmployeeLecturer', formData.courseName],
    queryFn: () => courseService.getEmployeeLecturerData({ isEmployee: true, isLecturer: true })
  });

  const { data: employeeLecturerData, isPending: isLoadingEmployeeLecturer } =
    fetchedEmployeeLecturer;

  const breadcrumbs = [
    { title: 'Course', href: pathnames.courses },
    { title: mode === 'add' ? 'New' : 'Edit' + ' ' + 'Course', href: '' }
  ];

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['lcmsDetail', id],
    queryFn: () => courseService.detail(id as string),
    select: (response) => response.data,
    enabled: Boolean(id && mode === 'edit')
  });

  const { data: dataMasterArea } = useQuery({
    queryKey: ['masterAreaServiceGetList'],
    queryFn: () => masterAreaService.getList({ page: 1, limit: 10000 }),
    staleTime: Infinity
  });

  const { data: dataCourseCategory } = useQuery({
    queryKey: ['courseServiceGetCourseCategories'],
    queryFn: () => courseService.getCourseCategories({ pageNumber: 1, pageSize: 10000 })
  });

  const { data: dataPassingFormula } = useQuery({
    queryKey: ['getPassingFormula'],
    queryFn: () => courseService.getPassingFormula(params),
    staleTime: Infinity
  });

  useEffect(() => {
    if (id) {
      refetch();
      setFormMode('edit');
    }
  }, [id]);

  useEffect(() => {
    if (data && data?.data) {
      setupFormCourse(data?.data, dataPassingFormula?.data);
      setDetailCourse(data?.data);
    }
  }, [data, dataPassingFormula]);

  const createCourses = useMutation({
    mutationFn: (data: FormCreateCourseLCMS) => courseService.create(data),
    onSuccess: () => {
      // Clear TNA comparison / buffer data after successful submission
      try {
        localStorage.removeItem('courseCombineCompareIds');
        localStorage.removeItem('tnaCoursesBuffer');
      } catch {
        /* ignore */
      }
      showSuccessNotification({
        title: 'Courses Created',
        message: 'Your course has been created successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['lcmsCourses'] });

      navigate(pathnames.courses);
    },
    onError: (error: any) => {
      showErrorNotification({
        title:
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          'Failed when created course',
        message: 'Failed when created course, please try again later.'
      });
    }
  });

  const updateCourses = useMutation({
    mutationFn: (data: FormCreateCourseLCMS) => courseService.update(data),
    onSuccess: () => {
      // Clear TNA comparison / buffer data after successful update (optional; keep clean)
      try {
        localStorage.removeItem('courseCombineCompareIds');
        localStorage.removeItem('tnaCoursesBuffer');
      } catch {
        /* ignore */
      }
      showSuccessNotification({
        title: 'Courses Updated',
        message: 'Your course has been updated successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['lcmsCourses'] });

      navigate(pathnames.courses);
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed when update course',
        message:
          error?.response?.data?.message ||
          error?.response?.data?.data?.message ||
          'Failed when update course, please try again later.'
      });
    }
  });

  const handleSaveDraft = (data: any) => {
    let payload: any = {
      basicInformation: {},
      deliveryDetails: {},
      indicatorStandards: {},
      activities: [],
      reviewApproval: {},
      tnaCourses: []
    };

    payload = transformCoursePayload(data, payload);
    if (formData.tnaCourses && formData.tnaCourses.length) {
      payload.tnaCourses = formData.tnaCourses;
    }
    if (mode === 'add') {
      createCourses.mutate({ ...payload, status: 'Draft' });
    } else {
      updateCourses.mutate({ ...payload, msLCMSCourseID: id, status: 'Draft' });
    }
    showSuccessNotification({
      title: 'Draft Saved',
      message: 'Your course draft has been saved successfully.'
    });

    setTimeout(() => {
      navigate(pathnames.courses);
    }, 500);
  };

  const handleSubmit = (data: any) => {
    let payload: any = {
      basicInformation: {},
      deliveryDetails: {},
      indicatorStandards: {},
      activities: [],
      reviewApproval: {},
      creatorCode: '',
      tnaCourses: []
    };

    payload = transformCoursePayload(data, payload, dataMasterArea?.data, dataCourseCategory?.data);
    if (formData.tnaCourses && formData.tnaCourses.length) {
      payload.tnaCourses = formData.tnaCourses;
    }
    setFormPayload({ ...payload });
    open();
  };

  const onConfirm = () => {
    if (formPayload) {
      if (mode === 'add') {
        createCourses.mutate(formPayload);
      } else {
        updateCourses.mutate({ ...formPayload, msLCMSCourseID: id });
      }
    } else {
      showErrorNotification({
        title: 'Failed when create course',
        message: 'The course data is not available.'
      });
    }
  };

  const handleCopyCourse = async (courseId: string) => {
    try {
      const response = await courseService.detail(courseId);
      const courseData = response.data?.data;

      if (courseData) {
        setupFormCourse(courseData);
        setDetailCourse(courseData);

        showSuccessNotification({
          message: 'Course data copied successfully! You can now modify the fields as needed.'
        });
      }
    } catch (error: any) {
      showErrorNotification({
        message: error?.message || 'Failed to copy course data'
      });
    } finally {
      closeCopyModal();
    }
  };

  return (
    <>
      <Breadcrumb items={breadcrumbs as BreadcrumbItem[]} mb={32} />

      <Group justify='space-between' align='center' mb={30}>
        <Title order={2}>{mode === 'add' ? 'Create New' : 'Edit'} Course</Title>
        {mode === 'add' && (
          <Button variant='outline' leftSection={<IconCopy size={16} />} onClick={openCopyModal}>
            Copy From Other Course
          </Button>
        )}
      </Group>

      <CourseForm
        onFormSubmit={handleSubmit}
        onSaveAsDraft={handleSaveDraft}
        employeeLecturerData={employeeLecturerData}
        isLoadingEmployeeLecturer={isLoadingEmployeeLecturer}
      />

      <ModalConfirmation
        onClose={() => close()}
        onConfirm={onConfirm}
        opened={opened}
        courseName={formPayload?.basicInformation?.courseName}
        isLoading={createCourses.isPending || updateCourses.isPending}
        type={mode === 'add' ? 'submit' : 'update'}
        key={mode}
      />

      <CopyCourseModal
        opened={copyModalOpened}
        onClose={closeCopyModal}
        onCourseSelected={handleCopyCourse}
      />

      <LoadingOverlay visible={isLoading || createCourses.isPending || updateCourses.isPending} />
    </>
  );
};

export default CreateCourse;
